import { FormEvent, useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import "./document-generator.css";

type Version = {
  id: string;
  version_number: number;
  title: string;
  body_html: string;
  status: "draft" | "published" | "retired";
  change_note: string | null;
};
type Template = {
  id: string;
  name: string;
  document_type: "reservation" | "sale";
  source: "vorno" | "stand";
  legal_status: string;
  legal_note: string;
  branch_key: string | null;
  versions: Version[];
};
type Source = { id: string; label: string; vehicle_id: string };
type Generation = {
  id: string;
  rendered_title: string;
  status: string;
  source_type: string;
  archive_document_id: string | null;
  created_at: string;
  template_version: number;
  template_name: string;
};
type Center = {
  templates: Template[];
  reservations: Source[];
  sales: Source[];
  generations: Generation[];
};
type Preview = {
  title: string;
  html: string;
  template_name: string;
  version_number: number;
  legal_note: string;
};
const empty: Center = {
  templates: [],
  reservations: [],
  sales: [],
  generations: [],
};
const errorText = (error: unknown, fallback: string) =>
  error && typeof error === "object" && "message" in error
    ? String(error.message)
    : fallback;
export function DocumentGenerator({
  organizationId,
  canManage,
  canConfigure,
  onClose,
  onChanged,
}: {
  organizationId: string;
  canManage: boolean;
  canConfigure: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [center, setCenter] = useState<Center>(empty),
    [notice, setNotice] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    [view, setView] = useState<"generate" | "templates" | "history">(
      "generate",
    ),
    [type, setType] = useState<"reservation" | "sale">("reservation"),
    [templateId, setTemplateId] = useState(""),
    [sourceId, setSourceId] = useState(""),
    [preview, setPreview] = useState<Preview | null>(null),
    [editing, setEditing] = useState<Template | null | undefined>(undefined);
  const load = async () => {
    const { data, error } = await supabase.rpc("document_template_center", {
      p_organization_id: organizationId,
    });
    if (error) setNotice(error.message);
    else setCenter(data as Center);
  };
  useEffect(() => {
    void load();
  }, [organizationId]);
  const templates = useMemo(
      () => center.templates.filter((t) => t.document_type === type),
      [center.templates, type],
    ),
    sources = type === "reservation" ? center.reservations : center.sales;
  useEffect(() => {
    setTemplateId("");
    setSourceId("");
    setPreview(null);
  }, [type]);
  const selected = templates.find((t) => t.id === templateId),
    version = selected?.versions.find((v) => v.status === "published");
  const showPreview = async () => {
    if (!version || !sourceId)
      return setNotice("Escolha o modelo e o processo.");
    setBusy(true);
    const { data, error } = await supabase.rpc("preview_document_template", {
      p_organization_id: organizationId,
      p_version_id: version.id,
      p_source_type: type,
      p_source_id: sourceId,
    });
    setBusy(false);
    if (error) setNotice(error.message);
    else setPreview(data as Preview);
  };
  const generate = async () => {
    if (!version || !sourceId)
      return setNotice("Escolha o modelo e o processo.");
    setBusy(true);
    setNotice("A gerar e arquivar o documento…");
    const { data, error } = await supabase.functions.invoke(
      "generate-process-document",
      {
        body: {
          organization_id: organizationId,
          version_id: version.id,
          source_type: type,
          source_id: sourceId,
        },
      },
    );
    if (error || data?.error)
      setNotice(
        data?.error ?? error?.message ?? "Não foi possível gerar o documento.",
      );
    else {
      setNotice("Documento gerado e guardado no arquivo do processo.");
      setPreview(null);
      await load();
      await onChanged();
    }
    setBusy(false);
  };
  const openGenerated = async (g: Generation) => {
    if (!g.archive_document_id) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke(
      "open-archive-document",
      {
        body: {
          document_id: g.archive_document_id,
          purpose: "Consulta de documento gerado",
        },
      },
    );
    setBusy(false);
    if (error || !data?.signed_url)
      setNotice(
        data?.error ?? error?.message ?? "Não foi possível abrir o documento.",
      );
    else window.open(data.signed_url, "_blank", "noopener,noreferrer");
  };
  const saveTemplate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget),
      { data, error } = await supabase.rpc("save_document_template", {
        p_organization_id: organizationId,
        p_template_id: editing?.id ?? null,
        p_name: form.get("name"),
        p_document_type: form.get("type"),
        p_title: form.get("title"),
        p_body_html: form.get("body"),
        p_branch_key: form.get("branch") || null,
        p_change_note: form.get("note") || null,
        p_legal_ack: form.get("legal") === "on",
      });
    if (error) setNotice(error.message);
    else {
      const result = await supabase.rpc("publish_document_template_version", {
        p_version_id: data,
      });
      setNotice(result.error?.message ?? "Nova versão guardada e publicada.");
      if (!result.error) setEditing(undefined);
      await load();
    }
    setBusy(false);
  };
  const startEdit = (t?: Template) => setEditing(t ?? null);
  return (
    <div className="stock-dialog-backdrop document-engine-backdrop">
      <section className="document-engine" role="dialog" aria-modal="true">
        <header>
          <div>
            <p className="eyebrow">Motor documental</p>
            <h2>Gerar documentos</h2>
            <p>
              Os dados do processo são preenchidos automaticamente e o PDF fica
              arquivado com a versão usada.
            </p>
          </div>
          <button className="stock-close" onClick={onClose}>
            ×
          </button>
        </header>
        <nav>
          <button
            className={view === "generate" ? "active" : ""}
            onClick={() => setView("generate")}
          >
            Gerar
          </button>
          <button
            className={view === "templates" ? "active" : ""}
            onClick={() => setView("templates")}
          >
            Modelos
          </button>
          <button
            className={view === "history" ? "active" : ""}
            onClick={() => setView("history")}
          >
            Histórico
          </button>
        </nav>
        {notice && (
          <button
            className="stock-notice"
            role="status"
            onClick={() => setNotice(null)}
          >
            {notice} ×
          </button>
        )}
        {view === "generate" && (
          <div className="document-generate">
            <label>
              <span>Processo</span>
              <select
                value={type}
                onChange={(e) =>
                  setType(e.target.value as "reservation" | "sale")
                }
              >
                <option value="reservation">Reserva</option>
                <option value="sale">Venda</option>
              </select>
            </label>
            <label>
              <span>Modelo</span>
              <select
                value={templateId}
                onChange={(e) => {
                  setTemplateId(e.target.value);
                  setPreview(null);
                }}
              >
                <option value="">Escolha um modelo</option>
                {templates
                  .filter((t) =>
                    t.versions.some((v) => v.status === "published"),
                  )
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.source === "stand" ? " · Modelo próprio" : " · Vorno"}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              <span>{type === "reservation" ? "Reserva" : "Venda"}</span>
              <select
                value={sourceId}
                onChange={(e) => {
                  setSourceId(e.target.value);
                  setPreview(null);
                }}
              >
                <option value="">Escolha o processo</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            {selected && (
              <aside>
                <strong>{selected.legal_note}</strong>
                <span>
                  Versão publicada: {version?.version_number ?? "—"}
                  {selected.branch_key
                    ? ` · Filial ${selected.branch_key}`
                    : " · Todo o stand"}
                </span>
              </aside>
            )}
            <footer>
              <button
                className="stock-secondary"
                disabled={busy || !version || !sourceId}
                onClick={() => void showPreview()}
              >
                Pré-visualizar
              </button>
              {canManage && (
                <button
                  className="stock-primary"
                  disabled={busy || !version || !sourceId}
                  onClick={() => void generate()}
                >
                  {busy ? "A processar…" : "Gerar e arquivar"}
                </button>
              )}
            </footer>
          </div>
        )}
        {view === "templates" && (
          <div className="document-templates">
            <header>
              <div>
                <h3>Modelos disponíveis</h3>
                <p>
                  Uma alteração cria uma nova versão; documentos anteriores
                  conservam a versão original.
                </p>
              </div>
              {canConfigure && (
                <button className="stock-primary" onClick={() => startEdit()}>
                  + Modelo próprio
                </button>
              )}
            </header>
            {center.templates.map((t) => (
              <article key={t.id}>
                <div>
                  <strong>{t.name}</strong>
                  <span>
                    {t.document_type === "reservation" ? "Reserva" : "Venda"} ·{" "}
                    {t.source === "vorno" ? "Modelo Vorno" : "Modelo do stand"}
                  </span>
                  <small>{t.legal_note}</small>
                </div>
                <div>
                  <b>
                    {t.versions.find((v) => v.status === "published")
                      ? `Versão ${t.versions.find((v) => v.status === "published")?.version_number}`
                      : "Sem versão publicada"}
                  </b>
                  {canConfigure && t.source === "stand" && (
                    <button
                      className="stock-secondary"
                      onClick={() => startEdit(t)}
                    >
                      Nova versão
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
        {view === "history" && (
          <div className="document-history">
            {center.generations.length ? (
              center.generations.map((g) => (
                <button
                  key={g.id}
                  disabled={!g.archive_document_id}
                  onClick={() => void openGenerated(g)}
                >
                  <span>
                    <strong>{g.rendered_title}</strong>
                    <small>
                      {g.template_name} · versão {g.template_version}
                    </small>
                  </span>
                  <span>
                    <b data-status={g.status}>
                      {g.status === "generated"
                        ? "Gerado"
                        : g.status === "failed"
                          ? "Falhou"
                          : "A processar"}
                    </b>
                    <small>
                      {new Date(g.created_at).toLocaleString("pt-PT")}
                    </small>
                  </span>
                </button>
              ))
            ) : (
              <p>Ainda não existem documentos gerados.</p>
            )}
          </div>
        )}
        {preview && (
          <div className="document-preview">
            <header>
              <div>
                <p className="eyebrow">Pré-visualização</p>
                <h3>{preview.title}</h3>
                <small>
                  {preview.template_name} · versão {preview.version_number}
                </small>
              </div>
              <button className="stock-close" onClick={() => setPreview(null)}>
                ×
              </button>
            </header>
            <article dangerouslySetInnerHTML={{ __html: preview.html }} />
            <footer>
              <button
                className="stock-secondary"
                onClick={() => setPreview(null)}
              >
                Voltar
              </button>
              {canManage && (
                <button
                  className="stock-primary"
                  disabled={busy}
                  onClick={() => void generate()}
                >
                  Gerar e arquivar
                </button>
              )}
            </footer>
          </div>
        )}
        {editing !== undefined && (
          <div className="document-template-editor">
            <form onSubmit={saveTemplate}>
              <header>
                <div>
                  <p className="eyebrow">Modelo próprio</p>
                  <h3>{editing ? "Criar nova versão" : "Novo modelo"}</h3>
                  <p>
                    Use apenas os campos apresentados. O sistema preenche-os com
                    os dados do processo.
                  </p>
                </div>
                <button
                  type="button"
                  className="stock-close"
                  onClick={() => setEditing(undefined)}
                >
                  ×
                </button>
              </header>
              <div className="template-editor-grid">
                <label>
                  <span>Nome</span>
                  <input
                    name="name"
                    required
                    defaultValue={editing?.name ?? ""}
                  />
                </label>
                <label>
                  <span>Processo</span>
                <select
                  name="type"
                  defaultValue={editing?.document_type ?? "reservation"}
                >
                    <option value="reservation">Reserva</option>
                    <option value="sale">Venda</option>
                  </select>
                </label>
                <label>
                  <span>Filial</span>
                  <input
                    name="branch"
                    defaultValue={editing?.branch_key ?? ""}
                    placeholder="Vazio para todo o stand"
                  />
                </label>
                <label className="wide">
                  <span>Título do documento</span>
                  <input
                    name="title"
                    required
                    defaultValue={
                      editing?.versions[0]?.title ??
                      "Declaração — {{vehicle.registration}}"
                    }
                  />
                </label>
                <label className="wide">
                  <span>Conteúdo</span>
                  <textarea
                    name="body"
                    required
                    defaultValue={
                      editing?.versions[0]?.body_html ??
                      "<h1>Declaração</h1><p>Viatura: {{vehicle.make}} {{vehicle.model}}, matrícula {{vehicle.registration}}.</p><p>Cliente: {{customer.name}}.</p>"
                    }
                  />
                </label>
                <p className="template-fields wide">
                  <strong>Campos:</strong>{" "}
                  {
                    "{{stand.legal_name}} · {{stand.tax_identifier}} · {{stand.address}} · {{vehicle.make}} · {{vehicle.model}} · {{vehicle.registration}} · {{vehicle.vin}} · {{customer.name}} · {{customer.email}} · {{customer.phone}} · {{reservation.starts_at}} · {{reservation.expires_at}} · {{reservation.deposit}} · {{sale.date}} · {{sale.price}}"
                  }
                  <br />
                  <strong>Composição do pagamento:</strong>{" "}
                  {
                    "[[EACH payments]] {{item.method}} · {{item.amount}} · {{item.proof}} [[END EACH]]"
                  }
                </p>
                <label className="wide">
                  <span>Nota da versão</span>
                  <input name="note" placeholder="O que mudou nesta versão" />
                </label>
                <label className="plain-check wide">
                  <input name="legal" type="checkbox" required /> Confirmo que o
                  stand é responsável pelo conteúdo e adequação jurídica deste
                  modelo.
                </label>
              </div>
              <footer>
                <button
                  type="button"
                  className="stock-secondary"
                  onClick={() => setEditing(undefined)}
                >
                  Cancelar
                </button>
                <button className="stock-primary" disabled={busy}>
                  Guardar
                </button>
              </footer>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
